import React, { useCallback, useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive';
import {
    Button,
    Header, HeaderContent,
    Icon, Image,
    Input,
    Tab,
    Table
} from 'semantic-ui-react'
import { PageParams } from '../../app/models/common/PagedResult';
import { formatPhoneNumber, getRandomNumber } from '../../shared/utils';

import { createGridInitialState, getFullSizeWidth, handleGridNavigation, handleGridSort } from '../../shared/utils';

import { User } from "../../app/models/registration/user";
import { useStore } from "../../app/stores/store";
import HeaderSubHeader from "semantic-ui-react/dist/commonjs/elements/Header/HeaderSubheader";
import GridFooter from "../../shared/GridFooter";
import ManageUserDetailsModal from "./ManageUserDetailsModal";
import { toast } from "react-toastify";
import ManageUserEntitiesModal from "./ManageUserEntitiesModal";
import { useDebounce } from "../../shared/useDebounce";

function ManageUsersPage() {

    const [loading, setLoading] = useState(true);
    const [usersList, setUsersList] = useState<User[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const { adminStore } = useStore();
    const showFullSize = useMediaQuery({ query: `(${getFullSizeWidth()})` })
    const [showUserDetail, setShowUserDetail] = useState<boolean>(false);
    const [showUserEntities, setShowUserEntities] = useState<boolean>(false);
    const initialState: PageParams = createGridInitialState({
        sortIndex: 'LastName',
        sortOrder: 'ascending',
    });

    const [params, setParams] = useState<PageParams>(initialState);
    const [selectedUser, setSelectedUser] = useState<number | null>(null);
    const [searchValue, setSearchValue] = useState<string>("");
    const [search, setSearch] = useState<number>(0);
    const debounceSearch = useDebounce<number>(search, 250);


    const loadUserList = useCallback(() => {
        setLoading(true);
        adminStore.getAllUsers(params)
            .then((r) => {
                setTotalRecords(r.totalRecords);
                setUsersList(r.data);
            })
            .catch(err => toast.error(err, { theme: "colored" }))
            .finally(() => setLoading(false));
    }, [adminStore, params]);

    useEffect(() => {
        loadUserList();
    }, [loadUserList]);


    const handleSort = (clickedColumn: string) => {
        if (usersList.length === 0) return;
        setParams(handleGridSort(params, clickedColumn));
    }

    const gridChange = (updatedParams: PageParams) => {
        setParams(handleGridNavigation(params, updatedParams));
    }


    const searchChange = (event: any) => {
        setSearchValue(event.target.value);
        setSearch(getRandomNumber);
    }

    useEffect(() => {
        if (debounceSearch > 0) {
            setParams(prevState => ({ ...prevState, search: searchValue, pageIndex: 0 }));
            setSearch(0);
        }
    }, [debounceSearch, searchValue]);

    const viewUser = async (userId: number) => {
        setSelectedUser(userId);
        setShowUserDetail(true);
    };

    const viewUserEntities = async (userId: number) => {
        setSelectedUser(userId);
        setShowUserEntities(true);
    };

    const closeUserDetailModal = async () => {
        loadUserList();
        setSelectedUser(null);
        setShowUserDetail(false);
    }

    const closeUserEntitiesModal = async () => {
        setSelectedUser(null);
        setShowUserEntities(false);
    }

    return (
        <Tab.Pane attached={true} loading={loading}>
            <Input onChange={(e) => searchChange(e)} placeholder='Search...' icon='search' />

            {!showUserDetail && !showUserEntities &&
                <Table singleLine sortable selectable striped color={'blue'}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell width={10} textAlign={'center'} onClick={() => handleSort("FirstName")}

                                sorted={params.sortIndex === "FirstName" && usersList.length > 0 ? params.sortOrder : undefined}>
                                <Icon name={'users'} /> Users
                            </Table.HeaderCell>
                            <Table.HeaderCell width={1} onClick={() => handleSort("Email")}

                                sorted={params.sortIndex === "Email" && usersList.length > 0 ? params.sortOrder : undefined}>
                                <Icon name={'mail'} />Email</Table.HeaderCell>
                            <Table.HeaderCell width={1} onClick={() => handleSort("PhoneNumber")}

                                sorted={params.sortIndex === "PhoneNumber" && usersList.length > 0 ? params.sortOrder : undefined}>
                                <Icon name={'phone'} />Phone</Table.HeaderCell>
                            <Table.HeaderCell collapsing width={1} textAlign={'center'} ><Icon name={'lock'} />Enabled</Table.HeaderCell>
                            <Table.HeaderCell collapsing width={2} textAlign={'center'} ><Icon name={'settings'} />Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {usersList.map((user: User, i) => (
                            <Table.Row key={i} style={{ cursor: 'pointer' }} >
                                <Table.Cell onClick={() => viewUser(user.id)}>
                                    <Header as={'h4'} image>
                                        <Image src={'./assets/user.png'} avatar spaced='right' />
                                        <HeaderContent>
                                            {user.firstName} {user.lastName}
                                            <HeaderSubHeader>User ID: {user.id}</HeaderSubHeader>
                                        </HeaderContent>
                                    </Header>
                                </Table.Cell>
                                <Table.Cell>{user.email}</Table.Cell>
                                <Table.Cell>{formatPhoneNumber(user.phoneNumber)}</Table.Cell>
                                <Table.Cell textAlign={'center'}>
                                    <Icon name={!user.disabled ? 'unlock' : 'lock'} color={!user.disabled ? 'green' :'red'}></Icon>
                                </Table.Cell>
                                <Table.Cell textAlign={'center'}>
                                    <Button icon={'drivers license'} color={'green'}
                                        onClick={() => viewUser(user.id)} />
                                    <Button icon={'building'} color={'green'}
                                        onClick={() => viewUserEntities(user.id)} />
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                    <GridFooter colSpan={showFullSize ? 5 : 1} params={params} totalRecords={totalRecords}
                        onChange={gridChange} />

                </Table>
            }
            {showUserDetail &&
                <ManageUserDetailsModal show={showUserDetail} userId={selectedUser ?? 0} onClose={closeUserDetailModal} />
            }
            {showUserEntities &&
                <ManageUserEntitiesModal show={showUserEntities} userId={selectedUser ?? 0} onClose={closeUserEntitiesModal} />
            }
        </Tab.Pane>
    )
}

export default ManageUsersPage